import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MessageSquarePlus, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/hooks/use-api";
import { api, errorMessage } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/PageHeader";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { RequestStatusBadge } from "@/components/common/SeverityBadge";
import { ServiceRequestDialog } from "@/components/service/ServiceRequestDialog";
import { REQUEST_STATUS } from "@/lib/status";
import { fmtDate, fmtDateTime, fmtRelative } from "@/lib/format";

const RequestDetail = ({ req, open, onOpenChange, onUpdated }) => {
  const { isProxima } = useAuth();
  const [status, setStatus] = useState(req?.status);
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/service/requests/${req.id}`, { status, response: response || null });
      toast.success("Henvendelsen er oppdatert");
      onUpdated?.(data);
      setResponse("");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="service-request-detail">
        {req ? (
          <>
            <SheetHeader className="pr-8 text-left">
              <div className="flex items-center gap-2">
                <RequestStatusBadge status={req.status} />
                <span className="mono text-xs text-muted-foreground">{req.ref}</span>
              </div>
              <SheetTitle className="pt-1">{req.subject}</SheetTitle>
              <SheetDescription>
                {req.category_label} · Prioritet {req.priority === "hoy" ? "høy" : req.priority}
                {req.plant_name ? (
                  <>
                    {" · "}
                    <Link to={`/kraftverk/${req.plant_id}`} className="text-primary hover:underline">
                      {req.plant_name}
                    </Link>
                  </>
                ) : null}
              </SheetDescription>
            </SheetHeader>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{req.description}</p>
            <Separator className="my-4" />
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Registrert</dt><dd className="font-medium text-right">{fmtDateTime(req.created_at)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Kontakt</dt><dd className="font-medium text-right">{req.contact_name}<div className="text-xs text-muted-foreground font-normal">{req.phone} · {req.email}</div></dd></div>
              {req.preferred_time ? <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Ønsket tid</dt><dd className="font-medium text-right">{req.preferred_time}</dd></div> : null}
              {req.response ? <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Svar fra Proxima</dt><dd className="font-medium text-right">{req.response}</dd></div> : null}
            </dl>
            {req.history?.length ? (
              <div className="mt-5">
                <div className="eyebrow mb-2">Historikk</div>
                <ol className="space-y-2 border-l pl-3 text-xs">
                  {req.history.map((h, i) => (
                    <li key={i}>
                      <span className="font-medium">{REQUEST_STATUS[h.status]?.label || h.status}</span> · {fmtDateTime(h.ts)} · {h.by}
                      {h.note ? <div className="text-muted-foreground">{h.note}</div> : null}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            {isProxima ? (
              <div className="mt-5 space-y-3 rounded-2xl border border-border/80 bg-muted/40 p-4" data-testid="service-request-admin">
                <div className="text-sm font-medium">Oppdater status (Proxima)</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-background" data-testid="service-request-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REQUEST_STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={2} placeholder="Melding til kunden (valgfritt)" className="bg-background text-sm" data-testid="service-request-response" />
                <Button size="sm" onClick={save} disabled={busy} data-testid="service-request-save">
                  Lagre
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default function ServicePage() {
  const [plantId, setPlantId] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const plants = useApi("/plants");
  const requests = useApi("/service/requests", {});
  const recParams = useMemo(() => ({ plant_id: plantId === "all" ? undefined : plantId }), [plantId]);
  const records = useApi("/service/records", recParams, { keepPrevious: true });

  if (requests.isError) return <ErrorState message="Kunne ikke hente servicedata." onRetry={requests.refetch} />;
  const open = (requests.data?.requests || []).filter((r) => !["fullfort", "avvist"].includes(r.status));

  return (
    <div className="space-y-7" data-testid="service-page">
      <PageHeader
        eyebrow="Service"
        title="Service og henvendelser"
        description="Bestill oppdrag, følg opp henvendelser og se servicehistorikk fra Proxima. Første kobling mot en fremtidig serviceplattform."
        actions={
          <Button className="gap-2 rounded-xl" onClick={() => setDialogOpen(true)} data-testid="service-new-request-button">
            <MessageSquarePlus className="h-4 w-4" aria-hidden="true" /> Ny henvendelse / bestill oppdrag
          </Button>
        }
      />

      <Tabs defaultValue="requests">
        <TabsList className="tabs-line">
          <TabsTrigger value="requests" data-testid="service-tab-requests">
            Henvendelser {open.length ? <span className="metric ml-1.5 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">{open.length}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="service-tab-history">
            Servicehistorikk
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-5">
          <section className="surface surface-pad">
            <SectionTitle title="Mine henvendelser" description="Status oppdateres av Proxima service" />
            {requests.isLoading ? (
              <Skeleton className="mt-4 h-40 w-full" />
            ) : requests.data?.requests?.length ? (
              <ul className="mt-3 -mx-2 hairline-y" data-testid="service-requests-list">
                {requests.data.requests.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => setSelected(r)} className="flex w-full items-center gap-3 rounded-xl px-2 py-3 text-left transition-[background-color] duration-150 hover:bg-muted/60" data-testid={`service-request-row-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{r.subject}</span>
                          <RequestStatusBadge status={r.status} />
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          <span className="mono">{r.ref}</span> · {r.category_label}
                          {r.plant_name ? ` · ${r.plant_name}` : ""}
                          {r.customer_name && !r.plant_name ? ` · ${r.customer_name}` : ""} · {fmtRelative(r.created_at)}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={MessageSquarePlus} title="Ingen henvendelser ennå" description="Bestill service, meld fra om feil eller still et spørsmål til Proxima." action="Ny henvendelse" onAction={() => setDialogOpen(true)} className="mt-4" testId="service-requests-empty" />
            )}
          </section>
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <section className="surface surface-pad">
            <SectionTitle
              title="Servicehistorikk"
              description="Utførte oppdrag registrert av Proxima"
              actions={
                <Select value={plantId} onValueChange={setPlantId}>
                  <SelectTrigger className="h-9 w-[220px] rounded-xl text-xs" data-testid="service-history-plant-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle kraftverk</SelectItem>
                    {(plants.data?.plants || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
            />
            {records.isLoading ? (
              <Skeleton className="mt-4 h-64 w-full" />
            ) : records.data?.records?.length ? (
              <div className="mt-3 overflow-x-auto scroll-thin">
                <Table data-testid="service-history-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Dato</TableHead>
                      <TableHead>Kraftverk</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="hidden lg:table-cell">Beskrivelse</TableHead>
                      <TableHead>Tekniker</TableHead>
                      <TableHead className="text-right">Timer</TableHead>
                      <TableHead className="text-right">Rapport</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.data.records.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="metric text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                        <TableCell className="text-sm">
                          <Link to={`/kraftverk/${r.plant_id}`} className="font-medium hover:text-primary">
                            {r.plant_name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="inline-flex items-center gap-1.5">
                            <Wrench className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> {r.type}
                          </span>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-md">{r.description}</TableCell>
                        <TableCell className="text-xs">{r.technician}</TableCell>
                        <TableCell className="metric text-right text-xs">{r.hours}</TableCell>
                        <TableCell className="metric text-right text-xs">{r.report_ref}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <EmptyState title="Ingen servicehistorikk" className="mt-4" />
            )}
          </section>
        </TabsContent>
      </Tabs>

      <ServiceRequestDialog open={dialogOpen} onOpenChange={setDialogOpen} plants={plants.data?.plants || []} onCreated={() => requests.refetch()} />
      <RequestDetail key={selected?.id} req={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} onUpdated={(r) => { setSelected({ ...selected, ...r }); requests.refetch(); }} />
    </div>
  );
}
